import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { ExportSkeleton } from "./ExportSkeleton";
import { exportAudience } from "@/api";
import {
  HelpText,
  SegmentBox,
  useActivePage,
  VariableSelection,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, MenuCard } from "@/components/molecules";
import { useAudience, useVariables } from "@/hooks";
import { AudienceSegment, Variable } from "@/types";
import {
  checkPermission,
  defaultSegment,
  exportOptions,
  getAudienceBreadcrumb,
  getRootPath,
  toAudienceType,
} from "@/utils";

const ExportPage: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();
  const path = toAudienceType(getRootPath(location.pathname));

  let { audienceId } = useParams<{ audienceId: string }>();
  const { audience, loading } = useAudience(audienceId, path);
  const audienceSharedToUser = audience?.shared;

  const [segment, setSegment] = useState<AudienceSegment>(defaultSegment);
  const [segmentNum, setSegmentNum] = useState<number>(0);

  const [exporting, setExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { variables } = useVariables();
  const [addVariables, setAddVariables] = useState<boolean>(false);
  const [addSummary, setAddSummary] = useState<boolean>(false);
  const [selection, setSelection] = useState<Variable[]>([]);

  useEffect(() => {
    if (audience) {
      audience.segments.length > 0 && setSegment(audience.segments[0]);
      setSelection(
        audience.audience_type === "build"
          ? audience.var_groups.map((v) => v.variables).flat()
          : []
      );
    }
  }, [audience]);

  useEffect(() => {
    if (audience && audience.segments.length > 0) {
      if (segment.segment_id !== 0) {
        setSegmentNum(
          audience.segments.map((x) => x.segment_id).indexOf(segment.segment_id)
        );
      } else {
        setSegmentNum(0);
      }
    }
  }, [audience, segment]);

  const handleSegmentSelect = (event: SelectChangeEvent) => {
    setSegment(
      audience?.segments.find(
        (option) => option.segment_name === event.target.value
      ) || defaultSegment
    );
  };

  const handleAddVariables = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddVariables(event.target.checked);
  };

  const handleAddSummary = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddSummary(event.target.checked);
  };

  const handleVariableSelect = (variables: Variable[]) => {
    setSelection(variables);
  };

  const handleVariableDelete = (variable: Variable) => {
    setSelection((prevState) =>
      prevState.filter((v) => v.tag_id !== variable.tag_id)
    );
  };

  const handleExport = async (exportOption: string, forceVariables: boolean | undefined) => {
    if (audience) {
      setExporting(true);
      setExportError(null);
      const accessToken = await getAccessTokenSilently();

      const { data, error } = await exportAudience(
        accessToken,
        audience.audience_id,
        audience.audience_name,
        audience.audience_type,
        audience.audience_geo,
        exportOption,
        (forceVariables || addVariables) && selection.length > 0 ? selection : [],
        forceVariables ? true : addSummary,
        segment
      );

      if (data) {
        window.open(data.url, "_blank");
        setExporting(false);
      }

      if (error) {
        setExportError(error.message);
        setExporting(false);
        console.error("Error exporting audience", error);
      }
    }
  };

  return (
    <PageContainer
      errors={{
        ExportError: exportError,
      }}
      loading={exporting}
      loadingText={"Preparing your data"}
      scope="export"
    >
      {loading ? (
        <ExportSkeleton />
      ) : (
        <Grid container spacing={2}>
          {audience && (
            <>
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audience.segments.length > 0 && (
                    <SegmentBox
                      label={"Segment"}
                      value={segment.segment_name}
                      options={audience.segments}
                      onChange={handleSegmentSelect}
                      showSelected={true}
                    />
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={audience.audience_size[segmentNum]}
                  />
                )}
              </Grid>
              {audience && audience.status === 'processing' ? (
                <Grid size={12}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    sx={{
                      pt: 4,
                      textAlign: 'center'
                    }}
                  >
                    This audience is still being processed, please
                    come back later or await a notification
                  </Typography>
                </Grid>
              ) : (
                <>
                  {audience && audience.audience_type !== 'mindset' && !audienceSharedToUser && (
                    <>
                      <Grid size={12}>
                        <HelpText
                          text="Configure your Download"
                          helpText="Configure the format and content of the file that you download."
                          variant="body1"
                        />
                      </Grid>
                      <Grid size={12}>
                        <Stack direction="row" spacing={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={addVariables}
                                size="small"
                                onChange={handleAddVariables}
                              />
                            }
                            disableTypography
                            label={
                              <HelpText
                                text="Add Variables"
                                helpText="Add the score for each variable to each geo as a column in the exported file"
                                variant="body2"
                              />
                            }
                          />
                          {addVariables && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={addSummary}
                                  size="small"
                                  onChange={handleAddSummary}
                                />
                              }
                              disableTypography
                              label={
                                <HelpText
                                  text="Add Summary Score"
                                  helpText="Create a single summary score for your each geo. For an Audience build this will summarise the variables in the criteria used to build the audience, otherwise it will average the scores for variables selected here."
                                  variant="body2"
                                />
                              }
                            />
                          )}
                        </Stack>
                      </Grid>
                      <Grid size={12}>
                        <Collapse in={addVariables}>
                          <Grid container size={12} spacing={1}>
                            <VariableSelection
                              variables={variables}
                              selection={selection}
                              onSelect={handleVariableSelect}
                              onDelete={handleVariableDelete}
                            />
                            <Grid size={12} />
                          </Grid>
                        </Collapse>
                      </Grid>
                    </>
                  )}
                  <Grid size={12}>
                    <HelpText
                      text="Select an Export Type"
                      helpText="Select the type of file you want to export. Each export type contains the geo data for your audiencein the most relevant format for the channel."
                      variant="body1"
                    />
                  </Grid>
                  <Grid container size={12} spacing={2} mb={4}>
                    {exportOptions.map((option, index) =>
                      (option.name === "lookalike" && audience.audience_type !== "upload")
                      || (option.name === 'national' && audience.audience_type === 'upload')
                      || (user && option.permission && !checkPermission(user, option.permission))
                        ? null : (
                        <Grid key={option.name}>
                          <MenuCard
                            key={index}
                            title={option.label}
                            text={option.description}
                            icon={option.icon}
                            disabled={
                              !audience.export ||
                              (addVariables && !option.allow_variables) ||
                              (option.force_variables && selection.length === 0)
                            }
                            onClick={() => handleExport(option.name, option.force_variables)}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default ExportPage;
